import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';

import ExpandOrgChartPlusIcon from '@components/Images/Rebranding/ExpandOrgChartPlusIcon';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';

import useOrgChart from './OrgChartContext';
import { OrgChartCompanyReportProps } from './interfaces';

const OrgChartCompanyReport = ({ employee, depth }: OrgChartCompanyReportProps) => {
  const {
    selectedEmployee,
    selectEmployee,
    isEmployeeExpanded,
    toggleEmployeeExpansion,
    employees: allEmployees,
  } = useOrgChart();

  const canExpand = employee.reports != null && employee.reports.length > 0;
  const hasReports = employee.reports != null && employee.reports.length > 0;
  const isSelected = selectedEmployee === employee.id;
  const isExpanded = isEmployeeExpanded(employee.id);

  const { trackEvent, Track } = useEventTracking({
    location: TrackingLocation.OrgChartCompanyReport,
  });

  const reports =
    employee.reports?.map((report) => allEmployees.find((e) => e.id === report)) ?? [];

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent(click({ clickTarget: ClickTarget.OrgChartCompanyReport, value: employee.id }));
    selectEmployee(employee.id);
    if (canExpand) {
      toggleEmployeeExpansion(employee.id);
    }
  };

  return (
    <Track>
      <InView
        onChange={(inView) => {
          inView &&
            trackEvent(
              impression({
                impressionElement: ImpressionElement.OrgChartCompanyReport,
                context: employee.id,
              })
            );
        }}
      >
        <div
          onClick={handleClick}
          className="relative mb-10 ml-[23px] mt-10 flex flex-col items-start justify-center hover:cursor-pointer"
          id={employee.id ? `employee-${employee.id}` : undefined}
        >
          <div className="w-full">
            <button
              id={`button-employee-${employee.id}`}
              type="button"
              className="relative mb-16 h-auto w-full appearance-none border-b-0 border-l-4 border-r-0 border-t-0 border-solid border-primary-magenta bg-white text-left"
            >
              <p className="m-0 px-16 text-caption-lg leading-tight">{employee.name}</p>
              <p className="m-0 px-16 text-caption-sm leading-tight text-grayscale-gray">
                {employee.title}
              </p>
              {canExpand && (
                <ExpandOrgChartPlusIcon
                  className={clsx(
                    'absolute -left-10 top-12 z-50 h-16 w-16 transform transition duration-300 hover:cursor-pointer',
                    {
                      'rotate-45 text-grayscale-gray': isExpanded,
                      'text-primary-magenta': !isExpanded,
                    }
                  )}
                />
              )}
              <div
                className={clsx(
                  'absolute -left-2 bottom-0 h-full transform bg-primary-magenta transition-all duration-300 ease-in-out',
                  {
                    'w-4': isSelected,
                    'w-0': !isSelected,
                  }
                )}
              ></div>
            </button>
          </div>

          {isExpanded && hasReports && (
            <div className="relative w-full">
              {/* Horizontal line - positioned to start after the vertical line */}
              <div
                className="absolute left-2 top-0 h-2 bg-primary-magenta"
                style={{ width: 'calc(100%)' }}
              ></div>

              {/* Vertical line */}
              <div className="absolute bottom-36 left-[25px] top-0 w-2 -translate-x-1/2 bg-primary-magenta"></div>

              <div className="relative py-8">
                {reports?.map(
                  (report) =>
                    report && (
                      <OrgChartCompanyReport depth={depth + 1} key={report.id} employee={report} />
                    )
                )}
              </div>
            </div>
          )}
        </div>
      </InView>
    </Track>
  );
};

export default OrgChartCompanyReport;
